import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, FileStack, Users, Settings, Activity,
  LogOut, Menu, X, GraduationCap,
} from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const roleColors = {
  admin: "bg-accent text-accent-foreground",
  manager: "bg-primary text-primary-foreground",
  hr: "bg-secondary text-secondary-foreground",
  trainer: "bg-muted text-muted-foreground",
  employee: "bg-muted text-muted-foreground",
};

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isEmployee = user?.role === "employee";

  const nav = [
    { to: "/dashboard", label: isEmployee ? "My Portal" : "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", show: true },
    { to: "/library", label: "Training Library", icon: BookOpen, testid: "nav-library-link", show: !isEmployee },
    { to: "/admin/templates", label: "Checklist Templates", icon: FileStack, testid: "nav-admin-templates-link", show: can(user, "edit_templates") },
    { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users-link", show: can(user, "manage_users") },
    { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-admin-settings-link", show: can(user, "edit_settings") },
    { to: "/admin/activity", label: "Activity Log", icon: Activity, testid: "nav-activity-link", show: can(user, "view_activity") },
  ];

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = () => (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
        <div className="flex items-center justify-center h-9 w-9 rounded-full bg-accent/20 border border-accent/30">
          <GraduationCap size={20} />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-extrabold text-[15px] tracking-tight">LOTUS ACADEMY</div>
          <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">Training Tracker</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.filter((n) => n.show).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]",
                isActive
                  ? "bg-accent/20 text-white"
                  : "text-sidebar-foreground/75 hover:bg-white/10 hover:text-white"
              )
            }
          >
            <n.icon size={18} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-accent text-accent-foreground text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.name}</div>
            <Badge className={cn("mt-0.5 text-[10px] capitalize px-1.5 py-0", roleColors[user?.role])}>
              {user?.role}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-button"
          onClick={logout}
          className="w-full justify-start gap-3 text-sidebar-foreground/75 hover:bg-white/10 hover:text-white mt-1"
        >
          <LogOut size={18} /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-[260px]"><SidebarInner /></div>
        </div>
      )}

      <div className="lg:pl-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 flex items-center justify-between h-16 px-4 sm:px-6 border-b bg-background/80 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" onClick={() => setOpen(true)}>
              <Menu size={20} />
            </Button>
            <div className="hidden sm:block">
              <Logo size="sm" showText={false} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto animate-fade-up">
          {children}
        </main>
      </div>
    </div>
  );
}
